import { useCallback, useEffect, useRef } from "react";

import { message } from "antd";
import { Edge, Node } from "reactflow";

import { clipboardService } from "Services/ClipboardService/ClipboardService";

import { CanvasClipboardData, CanvasType } from "./types";

export type CanvasCopyPasteConfig<T = unknown> = {
    canvasType: CanvasType;
    getSelectedElements: () => { nodes: Node[]; edges: Edge[] };
    onPaste: (data: { nodes: Node[]; edges: Edge[]; nodeData?: Record<string, T> }) => void;
    filterNodeBeforeCopy?: (node: Node) => boolean;
    getNodeData?: (nodeId: string) => T | undefined;
    canUseCopyPaste?: boolean;
};

/**
 * Custom hook to handle copy/paste functionality for canvas nodes
 * @param config Configuration object for copy/paste behavior
 */
export function useCanvasCopyPaste<T = unknown>(config: CanvasCopyPasteConfig<T>): void {
    // Use refs to avoid recreating event listeners
    const configRef = useRef(config);

    useEffect(() => {
        configRef.current = config;
    }, [config]);

    const canHandleEvent = useCallback((event: KeyboardEvent) => {
        const { canUseCopyPaste } = configRef.current;

        const target = event.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
            return false;
        }

        event.preventDefault();
        return canUseCopyPaste;
    }, []);

    const handleCopy = useCallback(
        (event: KeyboardEvent) => {
            if (!canHandleEvent(event)) {
                return;
            }

            const { canvasType, getSelectedElements, filterNodeBeforeCopy, getNodeData } = configRef.current;

            const { nodes, edges } = getSelectedElements();

            if (nodes.length === 0) {
                return;
            }

            // Filter nodes if filter function is provided
            const nodesToCopy = filterNodeBeforeCopy ? nodes.filter(filterNodeBeforeCopy) : nodes;

            if (nodesToCopy.length === 0) {
                return;
            }

            // Get node data if getter is provided
            const nodeData: Record<string, T> = {};
            if (getNodeData) {
                nodesToCopy.forEach((node) => {
                    const data = getNodeData(node.id);
                    if (data !== undefined) {
                        nodeData[node.id] = data;
                    }
                });
            }

            const clipboardData: CanvasClipboardData = {
                canvasType,
                nodes: nodesToCopy,
                edges,
                nodeData: Object.keys(nodeData).length > 0 ? nodeData : undefined,
            };

            clipboardService.copy(clipboardData);

            message.success("Copied to clipboard!");
        },
        [canHandleEvent]
    );

    const handlePaste = useCallback(
        (event: KeyboardEvent) => {
            if (!canHandleEvent(event)) {
                return;
            }

            const { canvasType, onPaste } = configRef.current;

            const clipboardData = clipboardService.paste<CanvasClipboardData>();

            if (!clipboardData || clipboardData.canvasType !== canvasType) {
                return;
            }

            const { nodes, edges, nodeData } = clipboardData;

            if (nodes.length === 0) {
                return;
            }

            onPaste({ nodes, edges, nodeData: nodeData as Record<string, T> | undefined });

            // Clear clipboard after successful paste
            clipboardService.clear();

            message.success("Pasted to canvas!");
        },
        [canHandleEvent]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // Check for Cmd/Ctrl + C (copy)
            if ((event.metaKey || event.ctrlKey) && event.key === "c" && !event.shiftKey && !event.altKey) {
                handleCopy(event);
            }

            // Check for Cmd/Ctrl + V (paste)
            if ((event.metaKey || event.ctrlKey) && event.key === "v" && !event.shiftKey && !event.altKey) {
                handlePaste(event);
            }
        },
        [handleCopy, handlePaste]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);
}
